import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  MapPin,
  Star,
  Loader2,
  Landmark,
  UtensilsCrossed,
  Building2,
  Trees,
  PartyPopper,
  ShoppingBag,
  Coffee,
  Hotel,
  ExternalLink,
} from "lucide-react";

const CATEGORIES = [
  { id: "attractions", name: "Attractions", icon: Landmark },
  { id: "restaurants", name: "Restaurants", icon: UtensilsCrossed },
  { id: "museums", name: "Museums", icon: Building2 },
  { id: "parks", name: "Parks", icon: Trees },
  { id: "cafes", name: "Cafes", icon: Coffee },
  { id: "nightlife", name: "Nightlife", icon: PartyPopper },
  { id: "shopping", name: "Shopping", icon: ShoppingBag },
  { id: "hotels", name: "Hotels", icon: Hotel },
];

export const POIDiscovery = ({ countryCode }) => {
  const [category, setCategory] = useState("attractions");
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) fetchPOIs();
  }, [category]);

  const fetchPOIs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/poi/${countryCode}?category=${category}`,
        { withCredentials: true }
      );
      setPlaces(res.data.places || []);
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const handleDiscover = () => {
    setLoaded(true);
    fetchPOIs();
  };

  const openInMaps = (place) => {
    window.open(
      `https://www.google.com/maps/place/?q=place_id:${place.place_id}`,
      "_blank"
    );
  };

  return (
    <div className="glass-card p-4" data-testid="poi-discovery">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-400" />
          Discover Places
        </h3>
        {!loaded && (
          <Button size="sm" className="btn-primary h-7 text-xs" onClick={handleDiscover} data-testid="discover-btn">
            Explore
          </Button>
        )}
      </div>

      {loaded && (
        <>
          {/* Category tabs */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1 scrollbar-hide">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${
                    category === cat.id
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-white/5 text-zinc-500 hover:text-zinc-300"
                  }`}
                  data-testid={`poi-tab-${cat.id}`}
                >
                  <Icon className="w-3 h-3" />
                  {cat.name}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
            </div>
          ) : places.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4">No places found for this category.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {places.map((place) => (
                <button
                  key={place.place_id}
                  onClick={() => openInMaps(place)}
                  className="w-full text-left flex gap-3 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                  data-testid={`poi-item-${place.place_id}`}
                >
                  {place.photo_url ? (
                    <img
                      src={place.photo_url}
                      alt={place.name}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Landmark className="w-6 h-6 text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <h4 className="text-sm font-medium text-white truncate">{place.name}</h4>
                      <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400 flex-shrink-0 mt-0.5" />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {place.rating && (
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                          <span className="text-xs text-amber-400">{place.rating}</span>
                          <span className="text-xs text-zinc-600">({place.total_ratings})</span>
                        </div>
                      )}
                      {place.open_now !== null && place.open_now !== undefined && (
                        <span className={`text-xs ${place.open_now ? "text-emerald-400" : "text-red-400"}`}>
                          {place.open_now ? "Open" : "Closed"}
                        </span>
                      )}
                      {place.price_level != null && (
                        <span className="text-xs text-zinc-500">
                          {"$".repeat(place.price_level || 1)}
                        </span>
                      )}
                    </div>
                    {place.address && (
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">{place.address}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
